import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Info, Package, Clock, TrendingDown, AlertCircle, CheckCircle, ShoppingCart, ChevronDown } from 'lucide-react';
import { trackEvent } from '../../utils/telemetry';

/**
 * Sessão 32 — QW3: "Why?" panel for procurement suggestions.
 *
 * Deterministic narrative explaining the suggestion using the SAME data
 * the algorithm used. Never opaque, never cosmetic.
 *
 * Per AI_ROLE_CHARTER.md §1.1 — must pass the "30-second rule":
 *   a novice operator should understand the suggestion in 30s.
 *
 * Props:
 *   data: {
 *     sku, product_name,
 *     current_stock, pending_quantity, cart_quantity, virtual_stock,
 *     average_daily_sales, virtual_days_until_stockout,
 *     lead_time_days, reorder_point, safety_stock, optimal_stock, moq,
 *     virtual_risk_level, suggested_order_quantity,
 *     pending_orders: [{ po_number, quantity, status }],
 *     client_allocation_qty, client_allocations,
 *     supplier
 *   }
 *   compact: boolean (default false) — show smaller button
 *   testid: optional id suffix
 */
export const WhyPanel = ({ data, compact = false, testid }) => {
  const [open, setOpen] = useState(false);
  // Sessão 33 (Phase α): detail sections collapsed by default — operator
  // sees the 1-sentence decisional summary first; expands only if needed.
  const [showDetails, setShowDetails] = useState(false);

  if (!data) return null;

  const {
    current_stock = 0,
    pending_quantity = 0,
    cart_quantity = 0,
    virtual_stock = 0,
    average_daily_sales = 0,
    virtual_days_until_stockout,
    lead_time_days = 7,
    reorder_point = 0,
    safety_stock = 0,
    optimal_stock = 0,
    moq = 1,
    virtual_risk_level = 'low',
    suggested_order_quantity = 0,
    pending_orders = [],
    client_allocation_qty = 0,
    client_allocations = [],
    supplier,
  } = data;

  // Compose the narrative reasons deterministically
  const signals = [];

  // 1. Stock pressure
  signals.push({
    kind: 'stock',
    icon: Package,
    title: 'Stock real',
    lines: [
      `Stock actual: ${current_stock} un.`,
      pending_quantity > 0 ? `Pendente em encomendas: +${pending_quantity} un.` : null,
      cart_quantity > 0 ? `No carrinho: +${cart_quantity} un.` : null,
      `Stock virtual (efectivo): ${virtual_stock} un.`,
    ].filter(Boolean),
  });

  // 2. Velocity / demand signal
  signals.push({
    kind: 'velocity',
    icon: TrendingDown,
    title: 'Velocidade de procura',
    lines:
      average_daily_sales > 0
        ? [
            `Vendas médias diárias (últimos 30 dias): ${average_daily_sales.toFixed(2)} un.`,
            virtual_days_until_stockout !== null && virtual_days_until_stockout !== undefined
              ? `Esgota em ~${virtual_days_until_stockout} dias se nada mudar`
              : null,
          ].filter(Boolean)
        : ['Sem histórico de vendas recentes — risco baseado apenas em stock actual'],
  });

  // 3. Lead time & supplier
  signals.push({
    kind: 'leadtime',
    icon: Clock,
    title: 'Fornecedor e lead time',
    lines: [
      supplier ? `Fornecedor: ${supplier}` : 'Sem fornecedor configurado',
      `Lead time esperado: ${lead_time_days} dias`,
      `MOQ: ${moq} un.`,
    ],
  });

  // 4. Thresholds
  signals.push({
    kind: 'thresholds',
    icon: AlertCircle,
    title: 'Limites configurados',
    lines: [
      `Reorder point: ${reorder_point} un.`,
      `Safety stock: ${safety_stock} un.`,
      optimal_stock > 0 ? `Stock óptimo: ${optimal_stock} un.` : null,
    ].filter(Boolean),
  });

  // 5. Customer allocations (if present) — F4 critical signal
  if (client_allocation_qty > 0) {
    signals.push({
      kind: 'allocations',
      icon: ShoppingCart,
      title: 'Alocações de cliente',
      lines: [
        `Procura materializada por clientes: ${client_allocation_qty} un.`,
        `Origens: ${client_allocations.length} entrada(s)`,
        'Estas quantidades já têm destino — pesam na sugestão.',
      ],
    });
  }

  // Sessão 33 (Phase α): one-sentence decisional summary at the top.
  // The 5 detail sections below are collapsed by default — operator
  // reads ONE line and decides. Drill-down is opt-in.
  const summarize = () => {
    const parts = [];
    if (virtual_risk_level === 'critical') {
      if (virtual_days_until_stockout != null) {
        parts.push(`Stock esgota em ~${virtual_days_until_stockout} dias`);
      } else {
        parts.push('Stock virtual abaixo de safety stock');
      }
    } else if (virtual_risk_level === 'high') {
      parts.push(`Stock virtual abaixo do reorder point (${reorder_point})`);
    } else if (virtual_risk_level === 'medium') {
      parts.push('Stock cobre menos de 14 dias');
    } else {
      parts.push('Stock saudável');
    }
    if (client_allocation_qty > 0) {
      parts.push(`${client_allocation_qty} un. já alocadas a cliente`);
    }
    if (suggested_order_quantity > 0) {
      parts.push(`sugerido ${suggested_order_quantity} un.`);
    }
    return parts.join(' · ');
  };

  const riskColors = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-amber-600',
    low: 'text-emerald-600',
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          // Sessão 33 (Phase β): observe operator behaviour — do they
          // actually open Why? panels? Which SKUs trigger curiosity?
          trackEvent('why_opened', {
            sku: data.sku,
            risk: virtual_risk_level,
            has_client_allocation: client_allocation_qty > 0,
          });
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          className={`${compact ? 'h-7 w-7' : 'h-7 px-2 text-xs'} text-slate-500 hover:text-slate-700`}
          data-testid={testid ? `why-panel-trigger-${testid}` : 'why-panel-trigger'}
          title="Porquê esta sugestão?"
        >
          <Info className={compact ? 'h-4 w-4' : 'h-3.5 w-3.5 mr-1'} />
          {!compact && <span>Porquê?</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 max-h-[80vh] overflow-y-auto bg-white border border-slate-200 shadow-xl"
        side="left"
        align="start"
        data-testid={testid ? `why-panel-content-${testid}` : 'why-panel-content'}
      >
        <div className="space-y-3">
          {/* Sessão 33 (Phase α): one-sentence decisional summary FIRST */}
          <div>
            <p className={`text-sm font-semibold ${riskColors[virtual_risk_level] || 'text-slate-700'}`}>
              {summarize()}
            </p>
            <p className="text-xs text-slate-500 mt-1 italic">
              A app sugere — tu decides.
            </p>
          </div>

          {/* Expandable details — collapsed by default for cognitive simplicity */}
          <button
            type="button"
            onClick={() => {
              setShowDetails((v) => {
                const next = !v;
                if (next) trackEvent('why_opened', { sku: data.sku, detail_expanded: true });
                return next;
              });
            }}
            className="w-full flex items-center justify-between text-xs text-slate-500 hover:text-slate-700 pt-2 border-t border-slate-100"
            data-testid={testid ? `why-panel-details-toggle-${testid}` : 'why-panel-details-toggle'}
          >
            <span>{showDetails ? 'Esconder detalhe' : 'Ver detalhe técnico'}</span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>

          {showDetails && (
            <>
              {/* Signals */}
              <div className="space-y-3">
                {signals.map((sig) => {
                  const Icon = sig.icon;
                  return (
                    <div key={sig.kind}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon className="h-3.5 w-3.5 text-slate-500" />
                        <p className="text-xs font-semibold text-slate-700">{sig.title}</p>
                      </div>
                      <ul className="text-xs text-slate-600 space-y-0.5 pl-5">
                        {sig.lines.map((line, i) => (
                          <li key={`${line}-${i}`}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Pending orders detail */}
              {pending_orders.length > 0 && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-700 mb-1">
                    <CheckCircle className="h-3 w-3 inline mr-1 text-blue-600" />
                    Encomendas em curso
                  </p>
                  <ul className="text-xs text-slate-600 space-y-0.5 pl-4">
                    {pending_orders.slice(0, 5).map((po, i) => (
                      <li key={po.po_number || i}>
                        {po.po_number} — {po.quantity} un. ({po.status})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {suggested_order_quantity > 0 && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-600">
                    Quantidade sugerida cobre ~{lead_time_days * 2} dias + safety stock,
                    arredondada ao MOQ ({moq}).
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WhyPanel;
